import React, { useCallback, useState } from 'react';
import { ChoiceElement, ChoiceOption } from '../../schema/choice.schema';
import styles from '../../assets/css/choice.module.css';
import { FormControl, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { FormDataValue } from '../../hooks/useFormData';
import { validateSelection } from './validation';
import ChevronIcon from './ChevronIcon';

interface MultipleChoiceProps {
	element: ChoiceElement;
	onChange: (key: string, value: FormDataValue) => void;
	onValidationChange: (key: string, value: boolean) => void;
}

const MultipleChoice: React.FC<MultipleChoiceProps> = ({ element, onChange, onValidationChange }) => {
	const { label, options, preselected, required, key, validator, placeholder } = element;

	const [selectedOptions, setSelectedOptions] = useState<Array<string>>(preselected || []);
	const [focused, setFocused] = useState<boolean>(false);
	const [isValid, setIsValid] = useState<boolean>(true);

	const handleChange = (event: SelectChangeEvent<Array<string>>) => {
		const selectedValues = event.target.value as Array<string>;
		setSelectedOptions(selectedValues);

		const isValid = validateSelection(selectedValues, validator, required);
		setIsValid(isValid.valid);
		onValidationChange(key, isValid.valid);

		onChange(key, selectedValues);
	};

	const handleFocus = useCallback(() => {
		setFocused(true);
	}, []);

	const handleBlur = useCallback(() => {
		setFocused(false);
	}, []);

	const style = {
		width: '100%',
		'& .MuiInputLabel-root': {
			color: '#707070'
		},
		'& .Mui-focused': {
			color: `${!isValid ? '#d32f2f' : '#007bff'}`,
			border: `1px solid ${!isValid ? '#d32f2f' : '#007bff'} !important`,
			borderRadius: '5px'
		},
		'& .MuiSelect-root': {
			borderBottomLeftRadius: '5px !important',
			borderBottomRightRadius: '5px !important'
		},
		'& .MuiInputBase-root': {
			'& .MuiInputBase-input:focus': {
				border: 'none !important',
				borderBottomLeftRadius: '5px',
				borderBottomRightRadius: '5px',
				borderTopLeftRadius: '5px !important',
				borderTopRightRadius: '5px !important'
			}
		},
		'&:focus-within': {
			border: 'none !important'
		}
	};

	return (
		<>
			<div className={styles['multiple-choice-container']}>
				<FormControl
					variant={label ? 'filled' : 'standard'}
					className={styles[label ? 'select-component-label' : 'select-component']}
					sx={style}
					fullWidth
					error={!isValid && focused}
				>
					<label className={`${styles['label-styles']} ${!isValid && focused ? styles['label-error'] : ''}`}>
						{label}
					</label>
					<Select
						id={key}
						multiple
						displayEmpty
						value={selectedOptions}
						onChange={handleChange}
						required={required}
						onFocus={handleFocus}
						className={styles['controlled-input']}
						onBlur={handleBlur}
						inputProps={{
							style: {
								color: `${!isValid ? '#FF0000' : '#707070'}`,
								fontWeight: '300'
							}
						}}
						SelectDisplayProps={{
							style: {
								color: `${!isValid ? '#FF0000' : '#707070'}`,
								fontWeight: '300'
							}
						}}
						IconComponent={ChevronIcon}
						renderValue={(selected) => {
							if (selected.length === 0) {
								return (
									<em
										style={{
											color: `${focused && !isValid ? '#FF0000' : '#707070'}`,
											fontWeight: '300'
										}}
										className={styles['placeholder']}
									>
										{placeholder}
									</em>
								);
							}
							return selected
								.map((value) => options.find((option) => option.value === value)?.label)
								.join(', ');
						}}
					>
						{options.map((option: ChoiceOption) => (
							<MenuItem
								key={option.value}
								value={option.value}
								disabled={option.disabled}
								style={{ color: '#707070', fontWeight: '300' }}
							>
								{option.label}
								{option.description && (
									<span className={styles['option-description']}>{option.description}</span>
								)}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</div>
		</>
	);
};

export default MultipleChoice;
